import { Button } from "@/components/ui/button";
import { fmt, VehicleFinancialProfile } from "@/hooks/useVehicleSim";
import { ArrowLeft } from "lucide-react";

interface Props {
  profile: VehicleFinancialProfile;
  totalMonthlyCost: number;
  monthlyRemaining: number;
  liquidAfter: number;
  dti: number;
  rating: 'strong' | 'reasonable' | 'overextended' | 'unhealthy';
  reasonMismatch: string | null;
  onProceed: () => void;
  onExit: () => void;
}

const RATING_CONFIG: Record<string, { label: string; color: string; desc: string }> = {
  strong: { label: 'Strong Purchase', color: 'bg-emerald-500/10 text-emerald-600', desc: 'This purchase fits your finances and preserves flexibility.' },
  reasonable: { label: 'Reasonable but Risky', color: 'bg-amber-500/10 text-amber-600', desc: 'This purchase is possible, but it leaves little room for emergencies.' },
  overextended: { label: 'Overextended', color: 'bg-orange-500/10 text-orange-600', desc: 'This choice puts you under unnecessary monthly pressure.' },
  unhealthy: { label: 'Financially Unhealthy Choice', color: 'bg-destructive/10 text-destructive', desc: 'This decision weakens your financial position significantly.' },
};

export default function AffordabilityScreen({ profile, totalMonthlyCost, monthlyRemaining, liquidAfter, dti, rating, reasonMismatch, onProceed, onExit }: Props) {
  const config = RATING_CONFIG[rating];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Affordability Analysis</h2>
        <p className="text-sm text-muted-foreground">How this purchase affects your financial health.</p>
      </div>

      <div className="glass-card glow-border p-5 space-y-4">
        <div className={`text-center py-3 rounded-xl ${config.color}`}>
          <p className="font-heading font-bold text-sm">{config.label}</p>
          <p className="text-xs mt-1 opacity-80">{config.desc}</p>
        </div>

        {reasonMismatch && (
          <div className="bg-amber-500/10 border border-amber-500/20 p-3 rounded-xl">
            <p className="text-xs text-amber-600 font-medium">⚠ Needs Mismatch: {reasonMismatch}</p>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Monthly Cash Flow After Purchase</span>
            <span className={`font-semibold ${monthlyRemaining < 0 ? 'text-destructive' : monthlyRemaining < 300 ? 'text-amber-600' : 'text-emerald-600'}`}>{fmt(monthlyRemaining)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Liquid Cash After Purchase</span>
            <span className={`font-semibold ${liquidAfter < 0 ? 'text-destructive' : 'text-foreground'}`}>{fmt(liquidAfter)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Debt-to-Income Ratio</span>
            <span className={`font-semibold ${dti > 0.45 ? 'text-destructive' : dti > 0.36 ? 'text-amber-600' : 'text-emerald-600'}`}>{(dti * 100).toFixed(0)}%</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Emergency Fund Status</span>
            <span className={`font-semibold ${profile.emergencyFund > 3000 ? 'text-emerald-600' : 'text-amber-600'}`}>{fmt(profile.emergencyFund)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Total Monthly Vehicle Cost</span>
            <span className="font-semibold text-foreground">{fmt(totalMonthlyCost)}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Button onClick={onProceed} className="w-full">Proceed to Negotiation</Button>
        <Button variant="ghost" onClick={onExit} className="w-full text-muted-foreground">
          Walk Away From This Purchase
        </Button>
        <p className="text-[10px] text-center text-muted-foreground italic pt-1">
          Your down payment, loan term, and vehicle choice are locked for this run.
        </p>
      </div>
    </div>
  );
}
